import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  BILLING_COUNTRY_OPTIONS,
  formatBillingPhoneInput,
  getBillingPhoneMaxLength,
  getBillingPhonePlaceholder,
  resolveBillingCountryCode,
  validateBillingPhone,
} from '../utils/billingPhone';
import {
  validateBillingCity,
  validateBillingPostalCode,
} from '../utils/validateBillingLocationFields';
import ViewportSelect from './ViewportSelect';

function buildFormState(initialValues = {}) {
  return {
    name: initialValues.name || '',
    email: initialValues.email || '',
    phone: initialValues.phone || '',
    address: initialValues.address || '',
    city: initialValues.city || '',
    postalCode: initialValues.postalCode || '',
    country: resolveBillingCountryCode(initialValues.country),
  };
}

const BillingDetailsForm = ({ onSubmit, initialValues = {}, defaultsLoading = false }) => {
  const [formData, setFormData] = useState(() => buildFormState(initialValues));
  const [phoneError, setPhoneError] = useState('');
  const [cityError, setCityError] = useState('');
  const [postalCodeError, setPostalCodeError] = useState('');
  const defaultsAppliedRef = useRef(false);

  useEffect(() => {
    if (defaultsLoading || defaultsAppliedRef.current) {
      return;
    }

    defaultsAppliedRef.current = true;
    setFormData(buildFormState(initialValues));
  }, [defaultsLoading, initialValues]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === 'phone') {
      const formattedPhone = formatBillingPhoneInput(value, formData.country);
      setFormData({
        ...formData,
        phone: formattedPhone,
      });
      if (phoneError) {
        setPhoneError('');
      }
      return;
    }

    if (name === 'city' && cityError) {
      setCityError('');
    }

    if (name === 'postalCode' && postalCodeError) {
      setPostalCodeError('');
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleCountryChange = (event) => {
    const country = event.target.value;
    setFormData((previous) => ({
      ...previous,
      country,
      phone: previous.phone
        ? formatBillingPhoneInput(previous.phone, country)
        : '',
    }));
    setPhoneError('');
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const cityValidation = validateBillingCity(formData.city);
    if (!cityValidation.valid) {
      setCityError(cityValidation.error);
    } else {
      setCityError('');
    }

    const postalCodeValidation = validateBillingPostalCode(formData.postalCode, formData.country);
    if (!postalCodeValidation.valid) {
      setPostalCodeError(postalCodeValidation.error);
    } else {
      setPostalCodeError('');
    }

    const phoneValidation = validateBillingPhone(formData.phone, formData.country);
    if (!phoneValidation.valid) {
      setPhoneError(phoneValidation.error);
    } else {
      setPhoneError('');
    }

    if (!cityValidation.valid || !postalCodeValidation.valid || !phoneValidation.valid) {
      return;
    }

    onSubmit({
      ...formData,
      city: cityValidation.normalized,
      postalCode: postalCodeValidation.normalized,
      phone: phoneValidation.normalized,
    });
  };

  const supportedCountry = resolveBillingCountryCode(formData.country);
  const phonePlaceholder = getBillingPhonePlaceholder(supportedCountry);
  const phoneDescribedBy = phoneError ? 'phone-help phone-error' : 'phone-help';
  const cityDescribedBy = cityError ? 'city-error' : undefined;
  const postalCodeDescribedBy = postalCodeError ? 'postal-code-error' : undefined;

  return (
    <div className="card shadow-sm mb-4 billing-details-form">
      <div className="card-header bg-light">
        <h5 className="mb-0">Datos de Facturación</h5>
      </div>
      <div className="card-body">
        {defaultsLoading && (
          <div
            className="billing-details-form__loading"
            role="status"
            aria-live="polite"
          >
            <div className="spinner-border text-primary" aria-hidden="true" />
            <span className="visually-hidden">Cargando datos de facturación...</span>
          </div>
        )}
        <form
          id="billing-form"
          aria-label="billing-form"
          onSubmit={handleSubmit}
          className={defaultsLoading ? 'billing-details-form__fields--loading' : undefined}
        >
          <div className="mb-3">
            <label htmlFor="name" className="form-label">Nombre Completo</label>
            <input
              type="text"
              className="form-control"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={defaultsLoading}
            />
          </div>

          <div className="mb-3">
            <label htmlFor="email" className="form-label">Email</label>
            <input
              type="email"
              className="form-control"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={defaultsLoading}
            />
          </div>

          <div className="mb-3">
            <label htmlFor="country" className="form-label">País</label>
            <ViewportSelect
              className="form-select"
              id="country"
              name="country"
              value={formData.country}
              onChange={handleCountryChange}
              required
              disabled={defaultsLoading}
            >
              {BILLING_COUNTRY_OPTIONS.map((countryOption) => (
                <option key={countryOption.code} value={countryOption.code}>
                  {countryOption.label}
                </option>
              ))}
            </ViewportSelect>
          </div>

          <div className="mb-3">
            <label htmlFor="phone" className="form-label">Teléfono</label>
            <input
              type="tel"
              className={`form-control${phoneError ? ' is-invalid' : ''}`}
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder={phonePlaceholder}
              maxLength={getBillingPhoneMaxLength(supportedCountry)}
              inputMode="tel"
              autoComplete="tel"
              aria-invalid={Boolean(phoneError)}
              aria-describedby={phoneDescribedBy}
              required
              disabled={defaultsLoading}
            />
            <div id="phone-help" className="form-text">
              Incluye el prefijo del país, por ejemplo {phonePlaceholder}.
            </div>
            {phoneError && (
              <div id="phone-error" className="invalid-feedback d-block" role="alert">
                {phoneError}
              </div>
            )}
          </div>

          <div className="mb-3">
            <label htmlFor="address" className="form-label">Dirección</label>
            <input
              type="text"
              className="form-control"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              disabled={defaultsLoading}
            />
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="city" className="form-label">Ciudad</label>
              <input
                type="text"
                className={`form-control${cityError ? ' is-invalid' : ''}`}
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                aria-invalid={Boolean(cityError)}
                aria-describedby={cityDescribedBy}
                required
                disabled={defaultsLoading}
              />
              {cityError && (
                <div id="city-error" className="invalid-feedback d-block" role="alert">
                  {cityError}
                </div>
              )}
            </div>
            <div className="col-md-6 mb-3">
              <label htmlFor="postalCode" className="form-label">Código Postal</label>
              <input
                type="text"
                className={`form-control${postalCodeError ? ' is-invalid' : ''}`}
                id="postalCode"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
                aria-invalid={Boolean(postalCodeError)}
                aria-describedby={postalCodeDescribedBy}
                required
                disabled={defaultsLoading}
              />
              {postalCodeError && (
                <div id="postal-code-error" className="invalid-feedback d-block" role="alert">
                  {postalCodeError}
                </div>
              )}
            </div>
          </div>

          <div className="mt-4">
            <button type="submit" className="btn btn-primary w-100" disabled={defaultsLoading}>
              Continuar con el pago
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

BillingDetailsForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    postalCode: PropTypes.string,
    country: PropTypes.string,
  }),
  defaultsLoading: PropTypes.bool,
};

export default BillingDetailsForm;
